import { FileText, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import Modal from '../../../../../components/shared/Modal/Modal';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, editAction, deleteAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { useToast } from '../../../../../hooks/useToast';
import { createBillingLine, deleteBillingLine, listBillingLines, updateBillingLine } from '../../../../../services/BillingLinesService';
import { BillingLine } from '../../../../../types/Store.types';
import '../../shared-modules.css';

/** Series de facturación: cada una fija la serie con la que facturan los TPV de las tiendas que la usan. */
export const FinanceBillingSeriesView = () => {
  const { notify } = useToast();
  const [lines, setLines] = useState<BillingLine[]>([]);
  const [editing, setEditing] = useState<BillingLine | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [series, setSeries] = useState('');
  const [invoiceSeries, setInvoiceSeries] = useState('');
  const [status, setStatus] = useState<BillingLine['status']>('active');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<BillingLine | null>(null);

  const reload = () => listBillingLines().then(setLines).catch(err => console.error('Error fetching billing lines:', err));
  useEffect(() => { reload(); }, []);

  const openForm = (line?: BillingLine) => {
    setEditing(line || null);
    setCode(line?.code || '');
    setName(line?.name || '');
    setSeries(line?.series || '');
    setInvoiceSeries(line?.invoiceSeries || '');
    setStatus(line?.status || 'active');
    setFormError('');
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!editing && !code.trim()) { setFormError('El código es obligatorio.'); return; }
    if (!name.trim()) { setFormError('El nombre es obligatorio.'); return; }
    if (!/^[A-Za-z0-9_-]{1,10}$/.test(series.trim())) {
      setFormError('La serie de tickets admite letras, números, guiones y guion bajo (máx. 10).');
      return;
    }
    if (invoiceSeries.trim() && !/^[A-Za-z0-9_-]{1,10}$/.test(invoiceSeries.trim())) {
      setFormError('La serie de facturas admite letras, números, guiones y guion bajo (máx. 10).');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = { code: code.trim(), name: name.trim(), series: series.trim(), invoiceSeries: invoiceSeries.trim(), status };
      if (editing) await updateBillingLine(editing.id, payload);
      else await createBillingLine(payload);
      notify('Facturación', editing ? 'Serie actualizada.' : 'Serie creada.', 'info');
      await reload();
      setFormOpen(false);
    } catch (err) {
      setFormError((err as Error)?.message || 'Error al guardar la serie.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteBillingLine(target.id);
      notify('Facturación', `Serie ${target.code} eliminada.`, 'info');
      await reload();
    } catch (err) {
      notify('Facturación', (err as Error)?.message || 'Error al eliminar la serie.', 'warning');
    }
  };

  const columns: ColumnDef<BillingLine>[] = [
    { id: 'code', header: 'CÓDIGO', render: (l) => <span className="sales-channel-entity-id">{l.code}</span> },
    { id: 'name', header: 'NOMBRE', render: (l) => <span>{l.name}</span> },
    { id: 'series', header: 'SERIE TICKETS', render: (l) => <span>{l.series}</span> },
    { id: 'invoiceSeries', header: 'SERIE FACTURAS', render: (l) => <span>{l.invoiceSeries || '—'}</span> },
    { id: 'stores', header: 'TIENDAS', render: (l) => <span>{l.storesCount}</span> },
    { id: 'status', header: 'ESTADO', render: (l) => <span className={`module-badge ${l.status === 'active' ? 'active' : 'pending'}`}>{l.status === 'active' ? 'ACTIVA' : 'INACTIVA'}</span> }
  ];

  return (
    <>
      <div className="module-toolbar">
        <button type="button" className="module-action-btn" onClick={() => openForm()} data-keep-detail>
          <Plus size={12} /> Nueva serie de facturación
        </button>
      </div>

      <MasterDetailLayout isOpen={formOpen}>
        <DataTable
          data={lines}
          columns={columns}
          title={<><FileText size={14} className="module-panel-title-icon" /> Series_De_Facturacion</>}
          subtitle={`TOTAL: ${lines.length}`}
          getRowId={(l) => l.id}
          emptyText="Ninguna serie de facturación"
          actionsHeader="ACCIONES"
          actionsColumn={(l) => (
            <RowActionsMenu actions={[editAction(() => openForm(l)), deleteAction(() => setDeleteTarget(l))]} />
          )}
        />

        {formOpen && (
          <SidebarForm
            title={editing ? 'Editar_Serie_Facturacion' : 'Alta_Serie_Facturacion'}
            expandable
            onClose={() => setFormOpen(false)}
            submitLabel={saving ? 'Guardando...' : editing ? 'Guardar' : 'Crear serie'}
            submitDisabled={saving}
            onSubmit={handleSave}
          >
            {formError && <div className="module-form-error"><span>{formError}</span></div>}
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Código *</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} disabled={Boolean(editing)} className="sidebar-form-input" placeholder="LF-01" />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Nombre *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="sidebar-form-input" />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Serie de tickets *</label>
              <input type="text" value={series} onChange={(e) => setSeries(e.target.value)} className="sidebar-form-input" placeholder="FC" maxLength={10} />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Serie de facturas</label>
              <input type="text" value={invoiceSeries} onChange={(e) => setInvoiceSeries(e.target.value)} className="sidebar-form-input" placeholder="FF (vacío = F + almacén)" maxLength={10} />
              <p style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)' }}>Las facturas tienen su propia numeración, distinta de la de los tickets.</p>
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Estado</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as BillingLine['status'])} className="sidebar-form-input">
                <option value="active">ACTIVA</option>
                <option value="inactive">INACTIVA</option>
              </select>
            </div>
          </SidebarForm>
        )}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteTarget}
        type="urgent"
        title="Eliminar serie de facturación"
        message={`¿Eliminar la serie "${deleteTarget?.code}"? Las tiendas que la usen quedarán sin serie de facturación.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default FinanceBillingSeriesView;
